/**
 * AslenProvider — platform-neutral React context shared by web and mobile:
 * Supabase client, auth session, memberships, current company + role,
 * reporting currency. Persistence goes through a small KV interface so the web
 * can use localStorage and mobile AsyncStorage.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AslenClient } from '../db';
import type { MemberRole } from '../constants';
import { listMemberships, type Membership } from '../api/platform';
import { qk } from './keys';

export interface KeyValueStore {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem?(key: string): void | Promise<void>;
}

export type ReportingCurrency = 'TRY' | 'USD' | 'EUR';

export interface AslenContextValue {
  client: AslenClient;
  session: Session | null;
  user: User | null;
  authReady: boolean;
  memberships: Membership[];
  membershipsLoading: boolean;
  /** memberships could not be loaded (offline, request failing) — not the same as having none */
  membershipsUnavailable: boolean;
  company: Membership['company'] | null;
  companyId: string | null;
  member: Membership | null;
  role: MemberRole | null;
  setCompanyId(id: string): void;
  reportingCurrency: ReportingCurrency;
  setReportingCurrency(c: ReportingCurrency): void;
  refreshMemberships(): Promise<void>;
}

const Ctx = createContext<AslenContextValue | null>(null);

const COMPANY_KEY = 'aslen.companyId';
const CURRENCY_KEY = 'aslen.reportingCurrency';

export function AslenProvider({
  client,
  store,
  children,
  previewSession,
}: {
  client: AslenClient;
  store: KeyValueStore;
  children: ReactNode;
  /** dev-only visual preview: fixed session, memberships come from the pre-seeded query cache */
  previewSession?: Session;
}) {
  const qc = useQueryClient();
  const [session, setSession] = useState<Session | null>(previewSession ?? null);
  const [authReady, setAuthReady] = useState(!!previewSession);
  const [companyId, setCompanyIdState] = useState<string | null>(null);
  const [reportingCurrency, setReportingCurrencyState] = useState<ReportingCurrency>('TRY');

  useEffect(() => {
    if (previewSession) return;
    let active = true;
    void client.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setAuthReady(true);
    });
    const { data: sub } = client.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setAuthReady(true);
      if (!s) qc.clear();
    });
    void Promise.resolve(store.getItem(COMPANY_KEY)).then((v) => active && v && setCompanyIdState(v));
    void Promise.resolve(store.getItem(CURRENCY_KEY)).then(
      (v) => active && (v === 'TRY' || v === 'USD' || v === 'EUR') && setReportingCurrencyState(v),
    );
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [client, store, qc, previewSession]);

  const userId = session?.user.id ?? null;
  const membershipsQuery = useQuery({
    queryKey: qk.memberships(userId),
    queryFn: () => listMemberships(client),
    enabled: !!userId,
    staleTime: 60_000,
  });
  const memberships = useMemo(() => membershipsQuery.data ?? [], [membershipsQuery.data]);

  const member = useMemo(
    () => memberships.find((m) => m.company_id === companyId) ?? memberships[0] ?? null,
    [memberships, companyId],
  );

  const setCompanyId = useCallback(
    (id: string) => {
      setCompanyIdState(id);
      void store.setItem(COMPANY_KEY, id);
    },
    [store],
  );

  const setReportingCurrency = useCallback(
    (c: ReportingCurrency) => {
      setReportingCurrencyState(c);
      void store.setItem(CURRENCY_KEY, c);
    },
    [store],
  );

  const refreshMemberships = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: qk.memberships(userId) });
  }, [qc, userId]);

  const value: AslenContextValue = {
    client,
    session,
    user: session?.user ?? null,
    authReady,
    memberships,
    membershipsLoading: !!userId && membershipsQuery.isLoading,
    membershipsUnavailable: !!userId && !membershipsQuery.data && (membershipsQuery.isError || membershipsQuery.isPaused),
    company: member?.company ?? null,
    companyId: member?.company_id ?? null,
    member,
    role: member?.role ?? null,
    setCompanyId,
    reportingCurrency,
    setReportingCurrency,
    refreshMemberships,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAslen(): AslenContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAslen must be used inside <AslenProvider>');
  return v;
}

/** Current company id; throws when used before a company is selected. */
export function useCompanyId(): string {
  const { companyId } = useAslen();
  if (!companyId) throw new Error('Şirket seçili değil');
  return companyId;
}
